import { Component, type ReactNode } from 'react';
import { Box, Button, Typography } from '@mui/material';
import i18n from '../i18n';

// App-wide error boundary. Without it, any single render throw blanks the whole
// SPA on every route (React unmounts the tree) with no way to recover — which
// is exactly how a stale bundle / bad data can lock a user out. This catches
// the throw, shows the message, and offers a reload.
interface State {
  error: Error | null;
}

export default class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: unknown) {
    // Surface in the console for debugging; no remote logging wired here.
    // eslint-disable-next-line no-console
    console.error('Render error caught by ErrorBoundary:', error, info);
  }

  render() {
    if (this.state.error) {
      return (
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            p: 3,
            textAlign: 'center',
          }}
        >
          <Typography variant="h5" fontWeight={700}>
            {i18n.t('errorBoundary.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 560 }}>
            {this.state.error.message || i18n.t('errorBoundary.unexpected')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <Button variant="contained" onClick={() => window.location.reload()}>
              {i18n.t('errorBoundary.reload')}
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                this.setState({ error: null });
                window.location.assign('/');
              }}
            >
              {i18n.t('errorBoundary.backHome')}
            </Button>
          </Box>
        </Box>
      );
    }
    return this.props.children;
  }
}
